import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Key, AlertTriangle, Eye, Shield, Users } from 'lucide-react';
import apiService from '../services/apiService';

const PermissionManagement = () => {
  const [permissions, setPermissions] = useState([]);
  const [filteredPermissions, setFilteredPermissions] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [permissionUsage, setPermissionUsage] = useState({ roles: [], users: [] });
  const [error, setError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    module: '',
    type: '',
    resource: '',
    active: true
  });

  useEffect(() => {
    loadPermissions();
    loadModules();
  }, []);

  useEffect(() => {
    filterPermissions();
  }, [permissions, searchTerm, selectedModule]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      apiService.refreshTokenFromStorage();
      const data = await apiService.getAllPermissions();
      setPermissions(data);
    } catch (error) {
      setError('Failed to load permissions');
      console.error('Error loading permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadModules = async () => {
    try {
      apiService.refreshTokenFromStorage();
      const data = await apiService.getDistinctModules();
      setModules(data);
    } catch (error) {
      console.error('Error loading modules:', error);
    }
  };

  const loadPermissionUsage = async (permissionId) => {
    try {
      // This would need to be implemented in the backend
      // For now, we'll show a placeholder
      setPermissionUsage({ roles: [], users: [] });
    } catch (error) {
      console.error('Error loading permission usage:', error);
      setPermissionUsage({ roles: [], users: [] });
    }
  };

  const filterPermissions = () => {
    let filtered = [...permissions];

    if (searchTerm) {
      filtered = filtered.filter(permission =>
        permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permission.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permission.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permission.module.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedModule) {
      filtered = filtered.filter(permission => permission.module === selectedModule);
    }

    setFilteredPermissions(filtered);
  };

  const handleCreate = () => {
    setModalMode('create');
    setSelectedPermission(null);
    setFormData({
      name: '',
      displayName: '',
      description: '',
      module: '',
      type: '',
      resource: '',
      active: true
    });
    setShowModal(true);
  };

  const handleEdit = (permission) => {
    setModalMode('edit');
    setSelectedPermission(permission);
    setFormData({
      name: permission.name,
      displayName: permission.displayName,
      description: permission.description || '',
      module: permission.module,
      type: permission.type || '',
      resource: permission.resource || '',
      active: permission.active
    });
    setShowModal(true);
  };

  const handleView = (permission) => {
    setModalMode('view');
    setSelectedPermission(permission);
    setShowModal(true);
  };

  const handleViewUsage = async (permission) => {
    setSelectedPermission(permission);
    await loadPermissionUsage(permission.id);
    setShowUsageModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const permissionData = {
        ...formData,
        name: formData.name.toUpperCase()
      };

      if (modalMode === 'create') {
        await apiService.createPermission(permissionData);
      } else {
        await apiService.updatePermission(selectedPermission.id, permissionData);
      }

      setShowModal(false);
      loadPermissions();
    } catch (error) {
      setError(error.message || 'Failed to save permission');
    }
  };

  const handleDelete = async (permissionId) => {
    if (window.confirm('Are you sure you want to delete this permission? This action cannot be undone.')) {
      try {
        await apiService.deletePermission(permissionId);
        loadPermissions();
      } catch (error) {
        setError('Failed to delete permission');
      }
    }
  };

  const formatDateTime = (dateTime) => {
    return new Date(dateTime).toLocaleString();
  };

  const getPermissionsByModule = () => {
    const grouped = {};
    filteredPermissions.forEach(permission => {
      if (!grouped[permission.module]) {
        grouped[permission.module] = [];
      }
      grouped[permission.module].push(permission);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Permission Management</h1>
          <p className="text-gray-600">Manage system permissions and access controls</p>
        </div>
        <button
          onClick={handleCreate}
          className="mt-4 sm:mt-0 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Permission</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search permissions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Module</label>
            <select
              value={selectedModule}
              onChange={(e) => setSelectedModule(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">All Modules</option>
              {modules.map(module => (
                <option key={module} value={module}>{module}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedModule('');
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Permissions Table/Cards */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {Object.keys(getPermissionsByModule()).length > 0 ? (
          <div className="space-y-6 p-6">
            {Object.entries(getPermissionsByModule()).map(([module, modulePermissions]) => (
              <div key={module} className="border border-gray-200 rounded-lg">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">{module}</h3>
                  <p className="text-sm text-gray-600">{modulePermissions.length} permissions</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permission</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type/Resource</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {modulePermissions.map((permission) => (
                        <tr key={permission.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                                  <Key className="h-5 w-5 text-green-600" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{permission.displayName}</div>
                                <div className="text-sm text-gray-500">{permission.name}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs">
                              {permission.description || 'No description'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {permission.type && (
                                <div>Type: <span className="font-medium">{permission.type}</span></div>
                              )}
                              {permission.resource && (
                                <div>Resource: <span className="font-medium">{permission.resource}</span></div>
                              )}
                              {!permission.type && !permission.resource && 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDateTime(permission.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleView(permission)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="View Permission"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleEdit(permission)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Edit Permission"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleViewUsage(permission)}
                                className="text-green-600 hover:text-green-900"
                                title="View Usage"
                              >
                                <Users className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(permission.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete Permission"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Key className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No permissions found</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new permission.</p>
          </div>
        )}
      </div>

      {/* Permission Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {modalMode === 'create' ? 'Add New Permission' : modalMode === 'edit' ? 'Edit Permission' : 'Permission Details'}
                    </h3>
                  </div>

                  {modalMode === 'view' ? (
                    // View Mode
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Permission Name</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedPermission?.name}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Display Name</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedPermission?.displayName}</p>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedPermission?.description || 'No description'}</p>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Module</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedPermission?.module}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Type</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedPermission?.type || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Resource</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedPermission?.resource || 'N/A'}</p>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedPermission?.active ? 'Active' : 'Inactive'}</p>
                      </div>
                    </div>
                  ) : (
                    // Create/Edit Mode
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Permission Name *</label>
                          <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="e.g., USER_CREATE"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                          <input
                            type="text"
                            required
                            value={formData.displayName}
                            onChange={(e) => setFormData({...formData, displayName: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="e.g., Create Users"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          rows={3}
                          value={formData.description}
                          onChange={(e) => setFormData({...formData, description: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="Describe what this permission allows..."
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Module *</label>
                          <input
                            type="text"
                            required
                            value={formData.module}
                            onChange={(e) => setFormData({...formData, module: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="e.g., User Management"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                          <select
                            value={formData.type}
                            onChange={(e) => setFormData({...formData, type: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          >
                            <option value="">Select Type</option>
                            <option value="CREATE">CREATE</option>
                            <option value="READ">READ</option>
                            <option value="UPDATE">UPDATE</option>
                            <option value="DELETE">DELETE</option>
                            <option value="EXECUTE">EXECUTE</option>
                            <option value="EXPORT">EXPORT</option>
                            <option value="IMPORT">IMPORT</option>
                            <option value="APPROVE">APPROVE</option>
                            <option value="VIEW_ALL">VIEW_ALL</option>
                            <option value="MANAGE">MANAGE</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Resource</label>
                          <input
                            type="text"
                            value={formData.resource}
                            onChange={(e) => setFormData({...formData, resource: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="e.g., user"
                          />
                        </div>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="active"
                          checked={formData.active}
                          onChange={(e) => setFormData({...formData, active: e.target.checked})}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                          Active
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  {modalMode !== 'view' && (
                    <button
                      type="submit"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      {modalMode === 'create' ? 'Create Permission' : 'Update Permission'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {modalMode === 'view' ? 'Close' : 'Cancel'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Usage Modal */}
      {showUsageModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Permission Usage - {selectedPermission?.displayName}
                  </h3>
                  <p className="text-sm text-gray-600">Roles and users that have this permission</p>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Roles</h4>
                    <div className="border border-gray-200 rounded-lg p-4 min-h-32">
                      {permissionUsage.roles.length > 0 ? (
                        <div className="space-y-2">
                          {permissionUsage.roles.map(role => (
                            <div key={role.id} className="flex items-center">
                              <Shield className="h-4 w-4 text-indigo-500 mr-2" />
                              <span className="text-sm text-gray-700">{role.displayName}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No roles have this permission</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Direct User Assignments</h4>
                    <div className="border border-gray-200 rounded-lg p-4 min-h-32">
                      {permissionUsage.users.length > 0 ? (
                        <div className="space-y-2">
                          {permissionUsage.users.map(user => (
                            <div key={user.id} className="flex items-center">
                              <Users className="h-4 w-4 text-green-500 mr-2" />
                              <span className="text-sm text-gray-700">{user.fullName}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No users have this permission directly assigned</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setShowUsageModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionManagement;